// Users tab — categorized user management dashboard.
// Categories: All / Free / Studio / Supporter / Parent / Kid / Business
// Actions: view profile, role change, gift, suspend, ban, reset password, wallet adjust.
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  Gift, Users as UsersIcon, Ban, KeyRound, Search, ShieldOff, Wallet, X,
  DollarSign, Megaphone, Baby, Shield, Sparkles, ExternalLink,
} from "lucide-react";
import api from "../../lib/api";
import { Modal } from "./Modal";
import { GrantPanel } from "./GrantPanel";

const CATEGORIES = [
  { id: "all",       label: "All",        icon: UsersIcon,  color: "#00F0FF" },
  { id: "free",      label: "Free",       icon: UsersIcon,  color: "rgba(255,255,255,0.6)" },
  { id: "studio",    label: "Studio",     icon: Sparkles,   color: "#FFC107" },
  { id: "supporter", label: "Supporter",  icon: Shield,     color: "#00FF66" },
  { id: "parent",    label: "Parent",     icon: Shield,     color: "#FF007F" },
  { id: "kid",       label: "Kid",        icon: Baby,       color: "#8B5CF6" },
  { id: "business",  label: "Business",   icon: Megaphone,  color: "#FFD700" },
];

const CATEGORY_COLOR = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.color]));

export function UsersTab() {
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/users/categorized", {
        params: { category, search: search.trim() || undefined },
      });
      setUsers(data.users || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [category]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const setRole = async (u, role) => {
    try { await api.post(`/owner/users/${u.id}/role`, { role }); toast.success(`${u.display_name} → ${role}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Role change failed"); }
  };
  const suspend = async (u) => {
    try { const { data } = await api.post(`/owner/users/${u.id}/suspend`); toast.success(data.suspended ? "Suspended" : "Reinstated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suspend failed"); }
  };
  const gift = async (u) => {
    try { await api.post(`/owner/users/${u.id}/gift`, { amount: 100 }); toast.success("+100 NX gifted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gift failed"); }
  };
  const viewProfile = async (u) => {
    try { const { data } = await api.get(`/owner/users/${u.id}/profile`); setViewing(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load profile"); }
  };

  const totalCount = counts.all || 0;

  return (
    <div className="space-y-4" data-testid="users-tab">
      {/* CATEGORY TABS */}
      <div className="flex flex-wrap gap-2" data-testid="user-category-tabs">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            data-testid={`user-cat-${c.id}`}
            className={`px-4 py-2 rounded-full border text-[10px] tracking-[0.25em] uppercase font-display transition-all flex items-center gap-2 ${
              category === c.id
                ? "border-transparent bg-white/10 text-white"
                : "border-white/10 text-white/50 hover:text-white hover:border-white/30"
            }`}
            style={category === c.id ? { boxShadow: `inset 0 0 0 1px ${c.color}, 0 0 24px ${c.color}44` } : {}}
          >
            <c.icon className="w-3.5 h-3.5" style={{ color: c.color }} />
            {c.label}
            <span className="text-[9px] font-mono opacity-70" data-testid={`user-cat-count-${c.id}`}>
              {counts[c.id] ?? "—"}
            </span>
          </button>
        ))}
      </div>

      {/* SEARCH BAR */}
      <div className="flex items-center gap-2 border border-white/10 rounded-full px-4 py-2 bg-black/30 max-w-md">
        <Search className="w-4 h-4 text-white/40" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or ID…"
          className="flex-1 bg-transparent text-sm outline-none placeholder-white/30"
          data-testid="user-search"
        />
        {search && (
          <button onClick={() => setSearch("")} data-testid="user-search-clear" className="text-white/40 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* STATUS ROW */}
      <div className="text-[11px] text-white/40">
        Showing <span className="text-nexus-cyan">{users.length}</span> of{" "}
        <span className="text-nexus-cyan">{category === "all" ? totalCount : counts[category] || 0}</span> ·{" "}
        {loading ? "loading…" : "live"}
      </div>

      {/* USER LIST */}
      <div className="space-y-px bg-white/5">
        {users.length === 0 && !loading && (
          <div className="bg-nexus-surface p-8 text-center text-white/40 text-sm" data-testid="users-empty">
            No users match this filter.
          </div>
        )}
        {users.map((u) => (
          <div key={u.id} className="bg-nexus-surface p-4 flex items-center gap-4 flex-wrap" data-testid={`user-row-${u.id}`}>
            <img src={u.avatar} alt="" className="w-10 h-10 ring-1 ring-white/20 rounded-full object-cover" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                <span className="truncate">{u.display_name}</span>
                {u.banned && <span className="text-nexus-crimson text-[9px] tracking-[0.3em] uppercase font-display bg-nexus-crimson/10 px-1.5 py-0.5">BANNED</span>}
                {u.suspended && !u.banned && <span className="text-nexus-amber text-[9px] tracking-[0.3em] uppercase font-display bg-nexus-amber/10 px-1.5 py-0.5">SUSPENDED</span>}
                <span
                  className="text-[9px] tracking-[0.2em] uppercase font-display px-1.5 py-0.5 rounded"
                  style={{ color: CATEGORY_COLOR[u._category], background: `${CATEGORY_COLOR[u._category]}18` }}
                  data-testid={`user-cat-badge-${u.id}`}
                >
                  {u._category}
                </span>
              </div>
              <div className="text-[11px] text-white/40 truncate">
                {u.email} · L{u.level || 0} · {(u.wallet_balance || 0).toFixed(0)} NX · KYC: {u.kyc_status || "—"}
                {u.paypal_email && <> · PP: {u.paypal_email}</>}
              </div>
            </div>
            <select
              value={u.role}
              onChange={(e) => setRole(u, e.target.value)}
              className="bg-transparent border border-white/10 px-2 py-1 text-xs rounded"
              data-testid={`role-${u.id}`}
            >
              <option value="viewer">viewer</option>
              <option value="creator">creator</option>
              <option value="sponsor">sponsor</option>
              <option value="staff">staff</option>
              <option value="owner">owner</option>
            </select>
            <button onClick={() => viewProfile(u)} className="btn-ghost !py-1.5 !px-3 text-[9px]" data-testid={`view-${u.id}`}>View</button>
            <button onClick={() => gift(u)} className="btn-ghost !py-1.5 !px-3 text-[9px]" data-testid={`gift-${u.id}`}>
              <Gift className="w-3 h-3" /> +100 NX
            </button>
            <button
              onClick={() => suspend(u)}
              className="btn-ghost !py-1.5 !px-3 text-[9px] !border-nexus-amber !text-nexus-amber"
              data-testid={`suspend-${u.id}`}
            >
              {u.suspended ? "Restore" : "Suspend"}
            </button>
          </div>
        ))}
      </div>

      {viewing && <UserProfileModal viewing={viewing} onClose={() => setViewing(null)} onRefresh={load} />}
    </div>
  );
}

// ============================================================
// Rich user profile modal — analytics, tax, payouts, actions.
// ============================================================
function UserProfileModal({ viewing, onClose, onRefresh }) {
  const [section, setSection] = useState("overview");
  const [banReason, setBanReason] = useState("");
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [adjustReason, setAdjustReason] = useState("");
  const [tempPassword, setTempPassword] = useState(null);

  const u = viewing.user;

  const doBan = async () => {
    if (!u.banned && !banReason.trim()) {
      toast.error("Ban requires a reason");
      return;
    }
    if (!window.confirm(u.banned ? "Un-ban this user?" : `Ban ${u.display_name}? This will also suspend the account.`)) return;
    try {
      const { data } = await api.post(`/owner/users/${u.id}/ban`, { reason: banReason.trim() });
      toast.success(data.banned ? "User banned" : "Ban lifted");
      setBanReason("");
      onRefresh();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ban action failed"); }
  };

  const doResetPassword = async () => {
    if (!window.confirm(`Reset password for ${u.display_name}? A temporary password will be shown once.`)) return;
    try {
      const { data } = await api.post(`/owner/users/${u.id}/reset-password`);
      setTempPassword(data.temp_password);
      toast.success("Password reset — share the temp password with the user.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Password reset failed"); }
  };

  const doAdjustWallet = async () => {
    const amt = parseFloat(adjustAmount);
    if (!Number.isFinite(amt) || amt === 0) { toast.error("Enter a non-zero NX amount"); return; }
    if (!window.confirm(`${amt > 0 ? "Credit" : "Debit"} ${Math.abs(amt)} NX ${amt > 0 ? "to" : "from"} ${u.display_name}?`)) return;
    try {
      const { data } = await api.post(`/owner/users/${u.id}/adjust-wallet`, { amount: amt, reason: adjustReason || "owner_adjustment" });
      toast.success(`Wallet: ${data.previous_balance.toFixed(0)} → ${data.new_balance.toFixed(0)} NX`);
      setAdjustAmount(0); setAdjustReason("");
      onRefresh(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Adjustment failed"); }
  };

  const SECTIONS = useMemo(() => [
    { id: "overview",  label: "Overview" },
    { id: "campaigns", label: `Campaigns (${viewing.campaigns?.length || 0})` },
    { id: "payouts",   label: `PayPal & Payouts (${viewing.payouts?.length || 0})` },
    { id: "invoices",  label: `Tax & Invoices (${viewing.coin_purchases?.length || 0})` },
    { id: "kids",      label: `Kids (${viewing.kids?.length || 0})` },
    { id: "actions",   label: "Actions" },
  ], [viewing]);

  return (
    <Modal onClose={onClose} title={`${u.display_name} · ${viewing.category}`}>
      {/* Section tabs */}
      <div className="flex flex-wrap gap-1 border-b border-white/10 -mx-6 px-6 pb-2 mb-1">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            data-testid={`profile-section-${s.id}`}
            className={`px-3 py-1.5 text-[9px] tracking-[0.25em] uppercase font-display ${section === s.id ? "text-nexus-cyan border-b border-nexus-cyan" : "text-white/40 hover:text-white"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === "overview" && (
        <div className="space-y-3 text-xs" data-testid="profile-overview">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="level"          value={`L${viewing.level.level}`} sub={`${viewing.level.total_xp} XP`} highlight />
            <Stat label="wallet"         value={`${(u.wallet_balance || 0).toFixed(0)} NX`} sub={`≈ $${viewing.wallet_balance_aud} AUD`} />
            <Stat label="streams"        value={viewing.streams.length} />
            <Stat label="subscribers"    value={viewing.active_subscribers} />
            <Stat label="lifetime tips"  value={`${viewing.lifetime_tips_nx.toFixed(0)} NX`} />
            <Stat label="breels"         value={viewing.breels.length} />
            <Stat label="kyc" value={viewing.kyc_status}
              color={viewing.kyc_status === "approved" ? "#00FF66" : viewing.kyc_status === "pending" ? "#FFC107" : "rgba(255,255,255,0.4)"} />
            <Stat label="paypal email"   value={viewing.paypal_email || "—"} tiny />
          </div>
          <div className="border-t border-white/10 pt-3 grid grid-cols-3 gap-2 text-[10px]">
            <div>{viewing.agreed_to_tos ? "✓" : "✗"} ToS</div>
            <div>{viewing.agreed_to_kyc ? "✓" : "✗"} KYC</div>
            <div>{viewing.agreed_to_billing ? "✓" : "✗"} Billing</div>
          </div>
          <div className="text-[11px] text-white/40 border-t border-white/10 pt-3">
            {u.email} · id: <span className="font-mono">{u.id}</span>
          </div>
          {(u.banned || u.suspended) && (
            <div className="border border-nexus-crimson/40 bg-nexus-crimson/10 p-3 rounded-lg" data-testid="user-status-warning">
              <div className="label-eyebrow text-nexus-crimson">// account status</div>
              {u.banned && <div className="text-sm mt-1">Banned{u.ban_reason ? ` · reason: ${u.ban_reason}` : ""}</div>}
              {u.suspended && !u.banned && <div className="text-sm mt-1">Suspended</div>}
            </div>
          )}
        </div>
      )}

      {section === "campaigns" && (
        <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="profile-campaigns">
          {(viewing.campaigns || []).length === 0 && <div className="text-white/40 text-xs">No campaigns.</div>}
          {(viewing.campaigns || []).map((c) => (
            <div key={c.id} className="border border-white/10 p-3 rounded-lg" data-testid={`campaign-${c.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium truncate">{c.name || c.title || "Untitled"}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: c.status === "active" ? "#00FF66" : "rgba(255,255,255,0.4)" }}>{c.status || "draft"}</div>
              </div>
              <div className="text-[11px] text-white/40 mt-1">
                Budget: ${(c.budget_aud || c.budget || 0).toFixed(2)} · Spent: ${(c.spent || 0).toFixed(2)} · Impr: {c.impressions || 0} · Clicks: {c.clicks || 0}
              </div>
            </div>
          ))}
        </div>
      )}

      {section === "payouts" && (
        <div className="space-y-3 text-xs" data-testid="profile-payouts">
          <div className="border border-white/10 p-3 rounded-lg">
            <div className="label-eyebrow">// paypal email</div>
            <div className="text-sm mt-1 font-mono">{viewing.paypal_email || "not_set"}</div>
          </div>
          <div className="label-eyebrow">// payout history</div>
          {(viewing.payouts || []).length === 0 && <div className="text-white/40">No payouts on record.</div>}
          {(viewing.payouts || []).map((p, i) => (
            <div key={p.id || i} className="border border-white/10 p-2.5 rounded" data-testid={`payout-${i}`}>
              <div className="flex items-center justify-between">
                <div className="font-mono text-sm">${(p.amount_aud || p.amount || 0).toFixed(2)} AUD</div>
                <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: p.status === "released" ? "#00FF66" : "#FFC107" }}>{p.status}</div>
              </div>
              <div className="text-[10px] text-white/40 mt-0.5">{p.paypal_email} · {new Date(p.ts).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      {section === "invoices" && (
        <div className="space-y-3 text-xs" data-testid="profile-invoices">
          <div className="border border-white/10 p-3 rounded-lg space-y-1">
            <div className="label-eyebrow text-nexus-amber">// tax record</div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div><span className="text-white/40">Country: </span>{viewing.tax_country || u.tax_country || "—"}</div>
              <div><span className="text-white/40">ID type: </span>{viewing.tax_info?.tax_id_type || "—"}</div>
              <div><span className="text-white/40">ID last4: </span><span className="font-mono">{viewing.tax_info?.tax_id_last4 ? `****${viewing.tax_info.tax_id_last4}` : "—"}</span></div>
              <div><span className="text-white/40">Status: </span>{u.tax_status || "not_submitted"}</div>
              <div><span className="text-white/40">Business: </span>{viewing.tax_info?.business_name || "—"}</div>
              <div><span className="text-white/40">Legal: </span>{u.legal_full_name || "—"}</div>
            </div>
          </div>
          <div className="label-eyebrow">// coin purchases (aud invoices)</div>
          {(viewing.coin_purchases || []).length === 0 && <div className="text-white/40">No coin purchases.</div>}
          {(viewing.coin_purchases || []).map((p, i) => (
            <div key={p.id || i} className="border border-white/10 p-2.5 rounded flex items-center justify-between" data-testid={`invoice-${i}`}>
              <div>
                <div className="font-mono text-sm">${(p.price || 0).toFixed(2)} · {p.coins || p.nx || 0} NX</div>
                <div className="text-[10px] text-white/40">{p.bundle_id || p.purpose} · {new Date(p.ts).toLocaleDateString()}</div>
              </div>
              {p.paypal_order_id && <span className="text-[9px] font-mono text-white/40">{String(p.paypal_order_id).slice(0, 12)}…</span>}
            </div>
          ))}
          {(viewing.wallet_adjustments || []).length > 0 && (
            <>
              <div className="label-eyebrow mt-3">// wallet adjustments</div>
              {viewing.wallet_adjustments.map((a, i) => (
                <div key={i} className="border border-white/10 p-2.5 rounded" data-testid={`adjust-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className={`font-mono text-sm ${a.delta_nx >= 0 ? "text-nexus-mint" : "text-nexus-crimson"}`}>
                      {a.delta_nx >= 0 ? "+" : ""}{a.delta_nx.toFixed(0)} NX
                    </div>
                    <div className="text-[10px] text-white/40">{new Date(a.ts).toLocaleString()}</div>
                  </div>
                  <div className="text-[10px] text-white/40 mt-0.5">{a.reason}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {section === "kids" && (
        <div className="space-y-2 text-xs" data-testid="profile-kids">
          {(viewing.kids || []).length === 0 && <div className="text-white/40">No linked kid accounts.</div>}
          {(viewing.kids || []).map((k) => (
            <div key={k.id} className="border border-white/10 p-3 rounded flex items-center gap-3" data-testid={`kid-${k.id}`}>
              <img src={k.avatar} alt="" className="w-8 h-8 rounded-full ring-1 ring-white/10" />
              <div className="flex-1 min-w-0">
                <div className="text-sm">{k.display_name}</div>
                <div className="text-[10px] text-white/40">Age {k.age || "—"} · L{k.level || 0} · {(k.wallet_balance || 0).toFixed(0)} NX</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {section === "actions" && (
        <div className="space-y-4 text-xs" data-testid="profile-actions">
          {/* Owner grant (existing) */}
          <div>
            <div className="label-eyebrow text-nexus-amber">// owner grant (studio tier, kyc override)</div>
            <GrantPanel uid={u.id} onDone={() => { onRefresh(); onClose(); }} />
          </div>

          {/* Wallet adjustment */}
          <div className="border border-white/10 p-3 rounded-lg" data-testid="wallet-adjust-card">
            <div className="label-eyebrow text-nexus-mint flex items-center gap-2"><Wallet className="w-3 h-3" />// approve wallet funds / adjust</div>
            <div className="text-[11px] text-white/50 mt-1">Positive credits, negative debits. Change is logged.</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <input
                type="number"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="+/- NX"
                className="col-span-1 bg-transparent border border-white/15 px-3 py-1.5 text-sm rounded"
                data-testid="wallet-adjust-amount"
              />
              <input
                type="text"
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="reason (topup approval, refund, etc.)"
                className="col-span-2 bg-transparent border border-white/15 px-3 py-1.5 text-sm rounded"
                data-testid="wallet-adjust-reason"
              />
            </div>
            <button
              onClick={doAdjustWallet}
              className="btn-nexus w-full mt-2 justify-center"
              data-testid="wallet-adjust-submit"
            >
              <DollarSign className="w-3.5 h-3.5" /> Apply
            </button>
          </div>

          {/* Reset password */}
          <div className="border border-white/10 p-3 rounded-lg" data-testid="reset-password-card">
            <div className="label-eyebrow text-nexus-cyan flex items-center gap-2"><KeyRound className="w-3 h-3" />// reset password</div>
            <div className="text-[11px] text-white/50 mt-1">Generates a one-time temp password shown once. Force user to change on next sign-in.</div>
            <button onClick={doResetPassword} className="btn-ghost mt-2 !text-nexus-cyan !border-nexus-cyan" data-testid="reset-password-btn">
              Generate temp password
            </button>
            {tempPassword && (
              <div className="mt-2 border border-nexus-cyan/50 bg-nexus-cyan/10 p-3 rounded" data-testid="temp-password-display">
                <div className="text-[10px] tracking-[0.2em] uppercase text-nexus-cyan">// share via secure channel</div>
                <div className="font-mono text-lg mt-1 select-all">{tempPassword}</div>
                <button
                  onClick={() => { navigator.clipboard?.writeText(tempPassword); toast.success("Copied"); }}
                  className="text-[10px] tracking-wider text-white/60 mt-1 hover:text-white"
                >
                  copy →
                </button>
              </div>
            )}
          </div>

          {/* Ban toggle */}
          <div className="border border-nexus-crimson/30 bg-nexus-crimson/5 p-3 rounded-lg" data-testid="ban-card">
            <div className="label-eyebrow text-nexus-crimson flex items-center gap-2"><Ban className="w-3 h-3" />// {u.banned ? "un-ban" : "ban"}</div>
            <div className="text-[11px] text-white/50 mt-1">{u.banned ? "Reinstate this account. All previous session tokens will be revoked on next login." : "Permanent-until-reversed. Suspends the account and records the reason."}</div>
            {!u.banned && (
              <input
                type="text"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="reason (required)"
                className="w-full bg-transparent border border-white/15 px-3 py-1.5 text-sm rounded mt-2"
                data-testid="ban-reason"
              />
            )}
            <button
              onClick={doBan}
              className="btn-ghost mt-2 !text-nexus-crimson !border-nexus-crimson"
              data-testid={u.banned ? "unban-btn" : "ban-btn"}
            >
              <ShieldOff className="w-3.5 h-3.5" /> {u.banned ? "Un-ban user" : "Ban user"}
            </button>
          </div>

          {/* Public profile link */}
          <a
            href={`/u/${u.id}`}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost inline-flex items-center gap-2 text-[10px]"
            data-testid="view-public-profile"
          >
            <ExternalLink className="w-3 h-3" /> Open public profile in new tab
          </a>
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, sub, color, highlight, tiny }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className={`font-display ${tiny ? "text-sm break-all" : "text-2xl"} mt-1`} style={color ? { color } : highlight ? { color: "#00F0FF" } : {}}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}
